import { getLogger } from "@core/logging/logger";
import { Store } from "@core/storage/storage";
import { NoTargetInfraError } from "@domain/infra/infra-errors";
import { InfrastructureConfig, ServerConfig } from "@domain/server-config/server-config";
import { ServerConfigManager } from "@domain/server-config/server-config-manager";
import { isNil, isNotNil } from "@utils/assert";
import { Signal } from "micro-signals";

type InfrastructureMap = Map<string, InfrastructureConfig>;

export class InfraManager {
	private readonly logger = getLogger("🛠 [InfraManager]");

	private _allInfras: InfrastructureMap = new Map();
	private _targetInfra: InfrastructureConfig | null = null;

	targetInfraChanged: Signal<InfrastructureConfig | null>;

	constructor(
		private readonly serverConfigManager: ServerConfigManager,
		private readonly store: Store<string>,
	) {
		this.serverConfigManager.configChanged.add(this.onServerConfigChanged.bind(this));
		this.targetInfraChanged = new Signal<InfrastructureConfig | null>();
	}

	get targetInfra(): InfrastructureConfig | null {
		return this._targetInfra;
	}

	get allInfras(): InfrastructureMap {
		return this._allInfras;
	}

	async init(): Promise<void> {
		const infrastructureId = this.store.getItem();
		try {
			await this.changeInfrastructureUsingId(infrastructureId);
		} catch (e) {
			this.logger.debug("Change infrastructure failed on init");
		}
	}

	async changeInfrastructureUsingCountryCode(countryCode: string): Promise<void> {
		const infrastructureId = this.findInfrastuctureByCountryCode(countryCode);
		if (isNil(infrastructureId)) {
			throw new Error(`Country code ${countryCode} is not supported by any infra`);
		}
		await this.changeInfrastructureUsingId(infrastructureId);
	}

	async changeInfrastructureUsingId(infrastructureId?: string): Promise<void> {
		if (isNotNil(infrastructureId)) {
			const targetConfig = this._allInfras.get(infrastructureId);
			if (isNil(targetConfig)) {
				throw new NoTargetInfraError(`Infra with code ${infrastructureId} does not exist`);
			}
			if (targetConfig.id !== this._targetInfra?.id) {
				this._targetInfra = targetConfig;
				this.store.setItem(infrastructureId);
			} else {
				this.logger.debug("Ignored infra change - same infra:", this.targetInfra?.name);
			}
		} else {
			this._targetInfra = null;
			this.store.clear();
		}
		this.logger.debug("Changed infra to", this.targetInfra?.name);
		this.targetInfraChanged.dispatch(this.targetInfra);
	}

	private findInfrastuctureByCountryCode(countryCode: string): string | undefined {
		for (const [infraId, infraConfig] of this._allInfras) {
			if (infraConfig.hostedCountries.includes(countryCode)) {
				return infraId;
			}
		}
	}

	private onServerConfigChanged(config: ServerConfig) {
		this._allInfras = new Map(config.infrastructures.map(infra => [infra.id, { ...infra }]));

		this.logger.debug(
			"Infrastructures defined: ",
			config.infrastructures.map(i => i.name),
		);
	}

	isInfrastructureCodeValid(infrastructureCode: string): boolean {
		return isNotNil(this._allInfras.get(infrastructureCode));
	}

	reset(): void {
		this.changeInfrastructureUsingId();
	}
}
